// SPDX-License-Identifier: AGPL-3.0-or-later

import type {AvatarCostumeDescriptor} from '@app/features/ui/avatars/AvatarCostumeResolvers';

/** Ink bounds as fractions of the costume element (viewBox) height. */
export interface CostumeInkBox {
	topRatio: number;
	bottomRatio: number;
}

const inkBoxByCostumeId = new Map<string, CostumeInkBox>();

export function getCostumeInkBox(costumeId: string): CostumeInkBox | undefined {
	return inkBoxByCostumeId.get(costumeId);
}

export function setCostumeInkBox(costumeId: string, ink: CostumeInkBox): void {
	inkBoxByCostumeId.set(costumeId, ink);
}

/** Test helper — clears the module-level ink cache. */
export function clearCostumeInkBoxCache(): void {
	inkBoxByCostumeId.clear();
}

/**
 * Reads SVG ink bounds via getBBox and stores them as viewBox-height fractions.
 * Returns null when the element has no measurable geometry.
 */
export function measureAndCacheCostumeInkBox(costumeId: string, svg: SVGSVGElement): CostumeInkBox | null {
	const existing = inkBoxByCostumeId.get(costumeId);
	if (existing != null) return existing;
	try {
		const box = svg.getBBox();
		const viewBox = svg.viewBox.baseVal;
		const height = viewBox.height > 0 ? viewBox.height : svg.height.baseVal.value;
		if (!(height > 0) || !(box.height > 0)) return null;
		// viewBox may not start at y=0 (e.g. wizard hat); normalize into the viewBox frame.
		const viewBoxY = viewBox.height > 0 ? viewBox.y : 0;
		const ink: CostumeInkBox = {
			topRatio: clampUnit((box.y - viewBoxY) / height),
			bottomRatio: clampUnit((box.y + box.height - viewBoxY) / height),
		};
		if (ink.bottomRatio < ink.topRatio) return null;
		inkBoxByCostumeId.set(costumeId, ink);
		return ink;
	} catch {
		return null;
	}
}

function clampUnit(value: number): number {
	if (!Number.isFinite(value)) return 0;
	return Math.min(1, Math.max(0, value));
}

const FULL_VIEWBOX_INK: CostumeInkBox = {topRatio: 0, bottomRatio: 1};

function resolveInkBox(descriptor: AvatarCostumeDescriptor): CostumeInkBox {
	if (descriptor.inkTopRatio != null || descriptor.inkBottomRatio != null) {
		const topRatio = clampUnit(descriptor.inkTopRatio ?? 0);
		const bottomRatio = clampUnit(descriptor.inkBottomRatio ?? 1);
		return {
			topRatio,
			bottomRatio: Math.max(topRatio, bottomRatio),
		};
	}
	// Full viewBox until getBBox refines — otherwise overhang stays 0 and scrunch never runs.
	return getCostumeInkBox(descriptor.id) ?? FULL_VIEWBOX_INK;
}

function costumeHeightPx(descriptor: AvatarCostumeDescriptor, size: number): number {
	const width = size * descriptor.widthRatio;
	if (!(descriptor.aspectRatio > 0)) return 0;
	return width / descriptor.aspectRatio;
}

function costumeElementTopPx(descriptor: AvatarCostumeDescriptor, size: number, height: number): number {
	switch (descriptor.placement) {
		case 'topper':
			return descriptor.offsetYRatio * height;
		case 'ring':
		case 'face-overlay':
			return size / 2 + descriptor.offsetYRatio * height - height / 2;
	}
}

/**
 * Where the avatar's top edge falls in the costume element, as a 0–1 fraction of height.
 * 0 = nothing above the avatar; 1 = the whole element is above the avatar.
 */
export function costumeAvatarTopInElementRatio(descriptor: AvatarCostumeDescriptor, size: number): number | null {
	const height = costumeHeightPx(descriptor, size);
	if (!(height > 0)) return null;
	return clampUnit(-costumeElementTopPx(descriptor, size, height) / height);
}

/**
 * How many px the costume ink extends above the avatar's top edge.
 * Uses the full viewBox until getBBox measures real ink (or manual ink* ratios).
 */
export function costumeOverhangPx(descriptor: AvatarCostumeDescriptor, size: number): number {
	const layout = costumeLayoutPx(descriptor, size);
	if (layout == null) return 0;
	return Math.max(0, -layout.inkTopRelativeToAvatarTop);
}

/**
 * Transform-origin Y relative to the avatar top (px). Positive = into the avatar.
 * Scaling from the ink bottom (not the avatar top) means fit must be
 * `(headroom + origin) / (overhang + origin)`, not `headroom / overhang`.
 */
export function costumeOriginBelowAvatarTopPx(descriptor: AvatarCostumeDescriptor, size: number): number {
	const layout = costumeLayoutPx(descriptor, size);
	if (layout == null) return 0;
	return layout.originRelativeToAvatarTop;
}

/** Fraction of element height for transform-origin Y (ink bottom). */
export function costumeInkBottomRatio(descriptor: AvatarCostumeDescriptor): number {
	return resolveInkBox(descriptor).bottomRatio;
}

function costumeLayoutPx(
	descriptor: AvatarCostumeDescriptor,
	size: number,
): {inkTopRelativeToAvatarTop: number; originRelativeToAvatarTop: number} | null {
	const ink = resolveInkBox(descriptor);
	const height = costumeHeightPx(descriptor, size);
	if (!(height > 0)) return null;

	const elementTop = costumeElementTopPx(descriptor, size, height);
	return {
		inkTopRelativeToAvatarTop: elementTop + ink.topRatio * height,
		originRelativeToAvatarTop: elementTop + ink.bottomRatio * height,
	};
}
